import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, MapPin, FileText, Eye } from 'lucide-react';
import { formatAmount } from '@/lib/currency';
import type { PublicBusinessContactInfo } from '@/lib/api';

interface DocumentSummaryProps {
  documentType: 'invoice' | 'quote';
  business: PublicBusinessContactInfo;
  documentNumber: string;
  documentDate: string | null;
  dueDate?: string | null;
  subtotal: number;
  shippingFee?: number;
  totalTax: number;
  totalAmount: number;
  // For invoices
  amountPaid?: number;
  balanceDue?: number;
  // For quotes
  depositAmount?: number;
  onViewDocument: () => void;
}

export function DocumentSummary({
  documentType,
  business,
  documentNumber,
  documentDate,
  dueDate,
  subtotal,
  shippingFee = 0,
  totalTax,
  totalAmount,
  amountPaid,
  balanceDue,
  depositAmount,
  onViewDocument,
}: DocumentSummaryProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isQuote = documentType === 'quote';

  return (
    <div className="bg-gray-50 rounded-lg p-6 space-y-6">
      {/* Business header */}
      <div className="flex items-start gap-4">
        {business.logo && (
          <img
            src={business.logo}
            alt={business.name}
            className="h-12 w-12 object-contain rounded"
          />
        )}
        <div>
          <h3 className="font-semibold text-gray-900">{business.name}</h3>
        </div>
      </div>

      <Separator />

      {/* Document details */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">{isQuote ? 'Estimate number' : 'Invoice number'}</span>
          <span className="font-medium text-gray-900">{documentNumber}</span>
        </div>
        {documentDate && (
          <div className="flex justify-between">
            <span className="text-gray-500">{isQuote ? 'Quote date' : 'Invoice date'}</span>
            <span className="font-medium text-gray-900">{formatDate(documentDate)}</span>
          </div>
        )}
        {dueDate && (
          <div className="flex justify-between">
            <span className="text-gray-500">{isQuote ? 'Expiration date' : 'Due date'}</span>
            <span className="font-medium text-gray-900">{formatDate(dueDate)}</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Totals */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Subtotal</span>
          <span className="text-gray-900">{formatAmount(subtotal)}</span>
        </div>
        {shippingFee > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Shipping</span>
            <span className="text-gray-900">{formatAmount(shippingFee)}</span>
          </div>
        )}
        {totalTax > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Tax</span>
            <span className="text-gray-900">{formatAmount(totalTax)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold">
          <span className="text-gray-900">{isQuote ? 'Estimate total' : 'Total'}</span>
          <span className="text-gray-900">{formatAmount(totalAmount)}</span>
        </div>

        {/* Invoice-specific: amount paid and balance */}
        {!isQuote && amountPaid !== undefined && amountPaid > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Amount paid</span>
            <span className="text-green-600">-{formatAmount(amountPaid)}</span>
          </div>
        )}

        {/* Quote-specific: deposit amount */}
        {isQuote && depositAmount !== undefined && depositAmount > 0 && (
          <div className="flex justify-between font-semibold text-primary">
            <span>Deposit amount</span>
            <span>{formatAmount(depositAmount)}</span>
          </div>
        )}

        {/* Balance due (for invoices) */}
        {!isQuote && balanceDue !== undefined && (
          <div className="flex justify-between font-semibold text-lg pt-2 border-t">
            <span className="text-gray-900">Balance due</span>
            <span className="text-gray-900">{formatAmount(balanceDue)}</span>
          </div>
        )}
      </div>

      {/* View document button */}
      <Button
        variant="outline"
        className="w-full"
        onClick={onViewDocument}
      >
        <Eye className="h-4 w-4 mr-2" />
        View {isQuote ? 'estimate' : 'invoice'}
      </Button>

      <Separator />

      {/* Contact info */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Contacts</p>
        <div className="space-y-2">
          {business.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-gray-400" />
              <a
                href={`mailto:${business.email}`}
                className="text-gray-600 hover:text-primary"
              >
                {business.email}
              </a>
            </div>
          )}
          {business.phone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-3.5 w-3.5 text-gray-400" />
              <a
                href={`tel:${business.phone}`}
                className="text-gray-600 hover:text-primary"
              >
                {business.phone}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
